import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import '../styles/Reports.css';
import { useAuth } from '../context/AuthContext';
import { useDownloads } from '../context/DownloadContext';

function Reports() {
  const { user } = useAuth();
  const { startDownload, refreshDownloads } = useDownloads();
  const isViewOnly = user?.phone === 'viewonlyuser';

  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState({});
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showOnlyMine, setShowOnlyMine] = useState(!isViewOnly);
  const viewerRef = useRef(null);
  const [highlightedReportId, setHighlightedReportId] = useState(null);
  const [reportsStats, setReportsStats] = useState({ total: 0, month: 0 });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [isDateFiltered, setIsDateFiltered] = useState(false);
  const [classificationStats, setClassificationStats] = useState({ toKitchen: 0, toBakery: 0, toKitchenBakery: 0, to: 0, av: 0, pnr: 0 });
  const [avSubStats, setAvSubStats] = useState({});
  const [avSubStatsFiltered, setAvSubStatsFiltered] = useState({});
  const [spentStats, setSpentStats] = useState({
    total: 0, month: 0, toKitchen: 0, toBakery: 0, toKitchenBakery: 0, to: 0, av: 0, pnr: 0,
    filteredTotal: 0, filteredToKitchen: 0, filteredToBakery: 0, filteredToKitchenBakery: 0, filteredTo: 0, filteredAv: 0, filteredPnr: 0,
  });
  const [selectedReports, setSelectedReports] = useState([]);
  const [pageSize] = useState(20); // Размер страницы
  const [isLoading, setIsLoading] = useState(false); // UI индикатор
  const [hasMore, setHasMore] = useState(true); // Есть ли еще страницы
  const sentinelRef = useRef(null); // Наблюдатель для бесконечной прокрутки
  const loadingRef = useRef(false); // фактическое состояние загрузки для защиты от гонок
  const activeRequestRef = useRef(0); // id последнего запроса
  const currentPageRef = useRef(1); // ref для отслеживания текущей страницы
  const previewCacheRef = useRef(new Map()); // Кэш превью картинок для избежания повторных загрузок
  const pagesCacheRef = useRef(new Map()); // Кэш списка страниц для избежания повторных запросов
  const [previewLoading, setPreviewLoading] = useState(false); // Индикатор загрузки превью
  const [previewGenerating, setPreviewGenerating] = useState(false); // Индикатор генерации превью
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionModalConfig, setActionModalConfig] = useState(null);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/users');
      const usersData = {};
      response.data.forEach(user => {
        usersData[user.id] = user;
      });
      setUsers(usersData);
    } catch (error) {
      console.error('Ошибка при загрузке пользователей', error);
    }
  }, []);

  const fetchReportsCount = useCallback(async () => {
    try {
      let url = "/api/reportscount";
      if (isDateFiltered && dateRange.startDate && dateRange.endDate) {
        url += `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      }
      const response = await axios.get(url);
      setReportsStats({
        total: response.data.total,
        month: response.data.month,
        filteredTotal: response.data.filteredTotal || 0,
        filteredMonth: response.data.filteredMonth || 0,
      });
      setClassificationStats({
        toKitchen: response.data.toKitchen,
        toBakery: response.data.toBakery,
        toKitchenBakery: response.data.toKitchenBakery,
        to: response.data.to,
        av: response.data.av,
        pnr: response.data.pnr,
        filteredToKitchen: response.data.filteredToKitchen || 0,
        filteredToBakery: response.data.filteredToBakery || 0,
        filteredToKitchenBakery: response.data.filteredToKitchenBakery || 0,
        filteredTo: response.data.filteredTo || 0,
        filteredAv: response.data.filteredAv || 0,
        filteredPnr: response.data.filteredPnr || 0,
      });
      setAvSubStats(response.data.avSubCategories || {});
      setAvSubStatsFiltered(response.data.filteredAvSubCategories || {});
      setSpentStats({
        total: response.data.totalSpentAmount || 0,
        month: response.data.monthSpentAmount || 0,
        toKitchen: response.data.toKitchenSpentAmount || 0,
        toBakery: response.data.toBakerySpentAmount || 0,
        toKitchenBakery: response.data.toKitchenBakerySpentAmount || 0,
        to: response.data.toSpentAmount || 0,
        av: response.data.avSpentAmount || 0,
        pnr: response.data.pnrSpentAmount || 0,
        filteredTotal: response.data.filteredTotalSpentAmount || 0,
        filteredToKitchen: response.data.filteredToKitchenSpentAmount || 0,
        filteredToBakery: response.data.filteredToBakerySpentAmount || 0,
        filteredToKitchenBakery: response.data.filteredToKitchenBakerySpentAmount || 0,
        filteredTo: response.data.filteredToSpentAmount || 0,
        filteredAv: response.data.filteredAvSpentAmount || 0,
        filteredPnr: response.data.filteredPnrSpentAmount || 0,
      });
    } catch (error) {
      console.error('Ошибка при загрузке статистики отчетов', error);
    }
  }, [isDateFiltered, dateRange.startDate, dateRange.endDate]);

  const fetchReports = useCallback(async (pageToLoad = 1, replace = false) => {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    const requestId = ++activeRequestRef.current;

    try {
      let url = `/api/reports?onlyMine=${showOnlyMine}&page=${pageToLoad}&pageSize=${pageSize}`;
      if (sortOrder) {
        url += `&order=${sortOrder}`;
      }
      if (isDateFiltered && dateRange.startDate && dateRange.endDate) {
        url += `&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const response = await axios.get(url);

      // Если пришел неактуальный ответ (устаревший запрос) — игнорируем
      if (requestId !== activeRequestRef.current) {
        return;
      }

      const newData = response.data.reports || [];
      const totalPages = response.data.totalPages || 1;

      setHasMore(pageToLoad < totalPages);

      if (replace) {
        setReports(newData);
        currentPageRef.current = pageToLoad;
      } else {
        setReports(prev => {
          // Создаем Set существующих ID для быстрой проверки
          const existingIds = new Set(prev.map(r => r.id));

          // Фильтруем только новые отчеты (которых еще нет)
          const newReports = newData.filter(r => !existingIds.has(r.id));

          // Добавляем новые отчеты в конец массива
          // Сервер уже вернул их в правильном порядке
          return [...prev, ...newReports];
        });
        currentPageRef.current = pageToLoad;
      }
    } catch (error) {
      console.error('Ошибка при загрузке отчетов', error);
      setError('Ошибка при загрузке отчетов');
    } finally {
      if (requestId === activeRequestRef.current) {
        setIsLoading(false);
        loadingRef.current = false;
      }
    }
  }, [showOnlyMine, isDateFiltered, dateRange.startDate, dateRange.endDate, searchTerm, pageSize, sortOrder]);

  const getUserFullName = (userId) => {
    if (!users[userId]) return 'Неизвестный пользователь';
    return `${users[userId].lastName || ''} ${users[userId].firstName || ''}`.trim();
  };

  const getPreviewName = (report) => {
    const base = report.filename.replace(/\.pdf$/i, '');
    return `${base}.png`;
  };

  // Функция загрузки одной страницы превью
  const loadPreviewPage = async (pageName) => {
    // Проверяем кэш картинок
    if (previewCacheRef.current.has(pageName)) {
      return previewCacheRef.current.get(pageName);
    }

    const response = await axios.get(`/api/reports/preview-image/${encodeURIComponent(pageName)}`, {
      responseType: 'blob',
      withCredentials: true,
    });
    const url = URL.createObjectURL(response.data);
    previewCacheRef.current.set(pageName, url);
    return url;
  };

  const handlePreviewClick = async (report) => {
    setSelectedReport(report);
    setShowPreview(true);
    setPreviewLoading(true);
    setPreviewGenerating(false);
    setError(''); // Сбрасываем предыдущую ошибку

    const previewName = getPreviewName(report);

    try {
      // Проверяем кэш списка страниц
      let pages = pagesCacheRef.current.get(previewName);

      if (!pages) {
        // Получаем список страниц превью с сервера
        const pagesResponse = await axios.get(`/api/reports/preview-pages/${encodeURIComponent(previewName)}`, {
          withCredentials: true,
        });
        pages = pagesResponse.data.pages || [];
        pagesCacheRef.current.set(previewName, pages);
      }

      if (pages.length === 0) {
        // Превью не найдено - пробуем сгенерировать
        setPreviewGenerating(true);
        setPreviewLoading(false);

        try {
          const regenResponse = await axios.post(`/api/reports/regenerate-preview/${encodeURIComponent(previewName)}`, {}, {
            withCredentials: true,
          });

          if (regenResponse.data.success && regenResponse.data.pages && regenResponse.data.pages.length > 0) {
            pages = regenResponse.data.pages;
            pagesCacheRef.current.set(previewName, pages);
            setPreviewGenerating(false);
            setPreviewLoading(true);
          } else {
            setError('Не удалось сгенерировать превью');
            setPreviewGenerating(false);
            return;
          }
        } catch (regenErr) {
          console.error('Ошибка регенерации превью:', regenErr);
          setError('Ошибка при генерации превью');
          setPreviewGenerating(false);
          return;
        }
      }

      // Проверяем, какие страницы уже в кэше
      const cachedUrls = [];
      for (const pageName of pages) {
        if (previewCacheRef.current.has(pageName)) {
          cachedUrls.push(previewCacheRef.current.get(pageName));
        } else {
          cachedUrls.push(null);
        }
      }

      // Загружаем первую страницу сразу (если не в кэше)
      const firstPageUrl = cachedUrls[0] || await loadPreviewPage(pages[0]);
      cachedUrls[0] = firstPageUrl;

      // Показываем страницы
      setTimeout(() => {
        const container = viewerRef.current;
        if (container) {
          container.innerHTML = '';

          // Создаём контейнеры для всех страниц заранее
          pages.forEach((pageName, index) => {
            const pageWrapper = document.createElement('div');
            pageWrapper.id = `preview-page-${index}`;
            pageWrapper.style.marginBottom = '16px';
            pageWrapper.style.borderBottom = index < pages.length - 1 ? '1px solid #dee2e6' : 'none';
            pageWrapper.style.paddingBottom = '16px';
            pageWrapper.style.minHeight = '200px'; // Placeholder height

            // Если URL уже в кэше, сразу показываем картинку
            if (cachedUrls[index]) {
              const img = document.createElement('img');
              img.src = cachedUrls[index];
              img.style.maxWidth = '100%';
              img.style.height = 'auto';
              img.style.display = 'block';
              pageWrapper.appendChild(img);
            } else {
              // Placeholder для страниц, которых нет в кэше
              const placeholder = document.createElement('div');
              placeholder.className = 'text-center text-muted py-4';
              placeholder.innerHTML = '<div class="spinner-border spinner-border-sm" role="status"></div> Загрузка страницы ' + (index + 1) + '...';
              pageWrapper.appendChild(placeholder);
            }

            container.appendChild(pageWrapper);
          });
        }
        setPreviewLoading(false);
      }, 0);

      // Загружаем только страницы, которых нет в кэше
      for (let i = 1; i < pages.length; i++) {
        if (!cachedUrls[i]) {
          const pageIndex = i;
          loadPreviewPage(pages[pageIndex]).then(url => {
            const pageWrapper = document.getElementById(`preview-page-${pageIndex}`);
            if (pageWrapper) {
              pageWrapper.innerHTML = '';
              const img = document.createElement('img');
              img.src = url;
              img.style.maxWidth = '100%';
              img.style.height = 'auto';
              img.style.display = 'block';
              pageWrapper.appendChild(img);
            }
          }).catch(err => {
            console.error(`Ошибка загрузки страницы ${pageIndex + 1}:`, err);
          });
        }
      }
    } catch (err) {
      setError('Ошибка при загрузке документа');
      console.error('Ошибка при загрузке документа:', err);
      setPreviewLoading(false);
    }
  };

  const handleSendTelegram = (filename) => {
    setShowTelegramModal(true);
    axios.post('/api/reports/send-telegram', { filename }).catch((error) => {
      setError(error.response?.data?.error || 'Ошибка при отправке в Telegram');
    });
  };

  const handleDelete = async (filename) => {
    if (window.confirm('Вы уверены, что хотите удалить этот отчет?')) {
      try {
        await axios.delete(`/api/reports/${encodeURIComponent(filename)}`);
        fetchReports(1, true);
        fetchReportsCount();
      } catch (error) {
        setError('Ошибка при удалении отчета');
        console.error('Ошибка при удалении отчета:', error);
      }
    }
  };

  const handleSendMonthlyTelegram = () => {
    setShowTelegramModal(true);
    axios.post('/api/reports/send-monthly-telegram').catch((error) => {
      setError(error.response?.data?.error || 'Ошибка при отправке в Telegram');
    });
  };

  const handleSendPeriodTelegram = () => {
    setShowTelegramModal(true);
    axios.get(`/api/reports/send-period-telegram?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`)
      .catch((error) => {
        setError(error.response?.data?.error || 'Ошибка при отправке в Telegram');
      });
  };

  const toggleReportSelection = (reportId) => {
    setSelectedReports((prev) =>
      prev.includes(reportId)
        ? prev.filter((id) => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleSendSelectedTelegram = () => {
    if (selectedReports.length === 0) {
      alert('Выберите хотя бы один отчет для отправки.');
      return;
    }
    setShowTelegramModal(true);
    axios.post('/api/reports/send-selected-telegram', { reportIds: selectedReports }).catch((error) => {
      setError(error.response?.data?.error || 'Ошибка при отправке в Telegram');
    });
  };

  const handleDownload = async (filename) => {
    try {
      const response = await axios.get(`/api/reports/file/${encodeURIComponent(filename)}`, { responseType: 'blob' });
      const fileName = filename.split('\\').pop().split('/').pop();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Ошибка при скачивании файла');
    }
  };

  const handleDownloadMonthlyReports = async () => {
    try {
      const response = await axios.get('/api/reports/monthly-zip');
      startDownload({ jobId: response.data.id, name: response.data.name, total: response.data.total });
    } catch (err) {
      setError(err?.response?.data?.error || 'Ошибка при скачивании архива отчетов');
    }
  };

  const handleDownloadReportsByPeriod = async () => {
    try {
      const response = await axios.get(
        `/api/reports/period-zip?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`
      );
      startDownload({ jobId: response.data.id, name: response.data.name, total: response.data.total });
    } catch (err) {
      setError(err?.response?.data?.error || 'Ошибка при скачивании архива отчетов за период');
    }
  };

  const handleDownloadSelectedReports = async () => {
    if (selectedReports.length === 0) {
      alert('Выберите хотя бы один отчет для скачивания.');
      return;
    }
    try {
      const response = await axios.post('/api/reports/download-selected', { reportIds: selectedReports });
      startDownload({ jobId: response.data.id, name: response.data.name, total: response.data.total });
    } catch (err) {
      setError(err?.response?.data?.error || 'Ошибка при скачивании выбранных отчетов');
    }
  };

  const openActionModal = (type, filename = null) => {
    setActionModalConfig({ type, filename });
    setShowActionModal(true);
  };

  const handleActionDownload = () => {
    setShowActionModal(false);
    const { type, filename } = actionModalConfig;
    if (type === 'single') handleDownload(filename);
    else if (type === 'monthly') handleDownloadMonthlyReports();
    else if (type === 'period') handleDownloadReportsByPeriod();
    else if (type === 'selected') handleDownloadSelectedReports();
  };

  const handleActionTelegram = () => {
    setShowActionModal(false);
    const { type, filename } = actionModalConfig;
    if (type === 'single') handleSendTelegram(filename);
    else if (type === 'monthly') handleSendMonthlyTelegram();
    else if (type === 'period') handleSendPeriodTelegram();
    else if (type === 'selected') handleSendSelectedTelegram();
  };

  useEffect(() => {
    fetchReports(1, true);
    fetchUsers();
    fetchReportsCount();
    // fetchReports стабилен по зависимостям выше
  }, [fetchUsers, fetchReportsCount, fetchReports]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedReportId(Number(highlightId));
      setTimeout(() => {
        const element = document.getElementById(`report-${highlightId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, []);

  // При заходе на страницу отчётов подтягиваем активные загрузки с сервера,
  // чтобы плашка появлялась без перезагрузки.
  useEffect(() => {
    refreshDownloads();
  }, [refreshDownloads]);

  // При изменении фильтров/поиска сбрасываем список и страницу
  useEffect(() => {
    currentPageRef.current = 1;
    setHasMore(true);
    setReports([]); // Сразу очищаем список
    loadingRef.current = false; // Сбрасываем флаг загрузки
    fetchReports(1, true);
    fetchReportsCount();
  }, [showOnlyMine, isDateFiltered, dateRange.startDate, dateRange.endDate, searchTerm, sortOrder, fetchReports, fetchReportsCount]);

  // IntersectionObserver для бесконечной прокрутки
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loadingRef.current) {
        const nextPage = currentPageRef.current + 1;
        fetchReports(nextPage, false);
      }
    }, { threshold: 0.1, rootMargin: '100px' });

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, fetchReports]);

  return (
    <div className="container mt-5">
      <h1>Отчеты</h1>
      <div className="d-flex gap-4 mb-3 align-items-center">
        {isDateFiltered ? (
          <div>
            <h3 style={{ marginBottom: '0.1rem' }}>Всего за этот период: {reportsStats.filteredTotal}</h3>
            {spentStats.filteredTotal > 0 && (
              <div style={{ fontSize: '0.85em', color: '#e67e22' }}>Затрачено: {spentStats.filteredTotal.toLocaleString('ru-RU')} руб.</div>
            )}
          </div>
        ) : (
          <>
            <div>
              <h3 style={{ marginBottom: '0.1rem' }}>За текущий месяц: {reportsStats.month}</h3>
              {spentStats.month > 0 && (
                <div style={{ fontSize: '0.85em', color: '#e67e22' }}>Затрачено: {spentStats.month.toLocaleString('ru-RU')} руб.</div>
              )}
            </div>
            <div>
              <h3 style={{ marginBottom: '0.1rem' }}>Всего отчетов: {reportsStats.total}</h3>
              {spentStats.total > 0 && (
                <div style={{ fontSize: '0.85em', color: '#e67e22' }}>Затрачено: {spentStats.total.toLocaleString('ru-RU')} руб.</div>
              )}
            </div>
          </>
        )}
        {isDateFiltered ? (
          <button className="btn btn-success" onClick={() => openActionModal('period')}>
            Скачать за период
          </button>
        ) : (
          <button className="btn btn-success" onClick={() => openActionModal('monthly')}>
            Скачать за месяц
          </button>
        )}
      </div>
      <div style={{ fontSize: '0.9em', marginBottom: '0.3rem' }}>
        {[
          { label: 'ТО Китчен', count: isDateFiltered ? classificationStats.filteredToKitchen : classificationStats.toKitchen, spent: isDateFiltered ? spentStats.filteredToKitchen : spentStats.toKitchen },
          { label: 'ТО Пекарня', count: isDateFiltered ? classificationStats.filteredToBakery : classificationStats.toBakery, spent: isDateFiltered ? spentStats.filteredToBakery : spentStats.toBakery },
          { label: 'ТО Китчен/Пекарня', count: isDateFiltered ? classificationStats.filteredToKitchenBakery : classificationStats.toKitchenBakery, spent: isDateFiltered ? spentStats.filteredToKitchenBakery : spentStats.toKitchenBakery },
          { label: 'АВ', count: isDateFiltered ? classificationStats.filteredAv : classificationStats.av, spent: isDateFiltered ? spentStats.filteredAv : spentStats.av },
          { label: 'ПНР', count: isDateFiltered ? classificationStats.filteredPnr : classificationStats.pnr, spent: isDateFiltered ? spentStats.filteredPnr : spentStats.pnr },
          { label: 'Прочие ТО', count: isDateFiltered ? classificationStats.filteredTo : classificationStats.to, spent: isDateFiltered ? spentStats.filteredTo : spentStats.to },
        ].map(({ label, count, spent }, idx, arr) => (
          <span key={label}>
            {label}: {count || 0}{spent > 0 ? ` (${(spent).toLocaleString('ru-RU')} руб.)` : ''}{idx < arr.length - 1 ? ' | ' : ''}
          </span>
        ))}
      </div>
      {(() => {
        const subData = isDateFiltered ? avSubStatsFiltered : avSubStats;
        const hasAny = Object.values(subData).some(v => v > 0);
        if (!hasAny) return null;
        return (
          <div style={{ fontSize: '0.8em', marginBottom: '1rem', color: '#ff6b6b' }}>
            <span style={{ fontWeight: 600 }}>АВ подкатегории: </span>
            {Object.entries(subData).filter(([, v]) => v > 0).map(([name, count], idx, arr) => (
              <span key={name}>{name}: {count}{idx < arr.length - 1 ? ' | ' : ''}</span>
            ))}
          </div>
        );
      })()}

      {error && <p className="text-danger">{error}</p>}

      <div className="row">
        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder="Поиск по адресу или дате..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <select className="form-select" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
            <option value="desc">Сначала новые</option>
            <option value="asc">Сначала старые</option>
          </select>
        </div>
        <div className="col-md-3">
          {isDateFiltered ? (
            <button
              className="btn btn-secondary w-100"
              onClick={() => {
                setIsDateFiltered(false);
                setDateRange({ startDate: '', endDate: '' });
              }}
            >
              Назад
            </button>
          ) : (
            <div className="form-check mt-2">
              <input
                type="checkbox"
                className="form-check-input me-2"
                id="showOnlyMine"
                checked={showOnlyMine}
                onChange={(e) => setShowOnlyMine(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showOnlyMine">
                Созданные вами отчеты
              </label>
            </div>
          )}
        </div>
        <div className="col-md-12 mt-3 d-flex align-items-center">
          <button
            className="btn btn-primary me-3"
            onClick={() => setShowDatePicker(true)}
          >
            Выбрать интервал
          </button>
          <button
            className="btn btn-success"
            onClick={() => openActionModal('selected')}
          >
            Скачать выбранное
          </button>
        </div>
      </div>

      <hr className="my-4" style={{ backgroundColor: '#dee2e6', height: '2px' }} />

      <div className="row">
        {reports.map((report) => (
          <div
            key={report.id}
            id={`report-${report.id}`}
            className={`col-md-4 mb-4 ${report.id === highlightedReportId ? 'highlight-card' : ''} ${selectedReports.includes(report.id) ? 'selected' : ''}`}
          >
            <div
              className={`card shadow-sm ${selectedReports.includes(report.id) ? 'border-primary' : ''}`}
              onClick={(e) => {
                if (!['BUTTON', 'INPUT'].includes(e.target.tagName)) {
                  toggleReportSelection(report.id);
                }
              }}
            >
              <div className="card-body position-relative">
                <input
                  type="checkbox"
                  className="form-check-input position-absolute top-0 end-0 m-2"
                  style={{ transform: 'scale(1.5)' }}
                  checked={selectedReports.includes(report.id)}
                  onChange={() => toggleReportSelection(report.id)}
                />
                <h5 className="card-title">Объект: {report.address}</h5>
                <div className="card-text d-flex justify-content-between align-items-center">
                  <span>Дата: {formatDate(report.date)}</span>
                  <span className="text-secondary" style={{ fontSize: '0.95em', textAlign: 'right', minWidth: '90px' }}>{report.classification}</span>
                </div>

                <p className="card-text text-muted" style={{ fontSize: '0.9em' }}>
                  {getUserFullName(report.userId || report.user_id)}
                </p>
                <div className="d-flex justify-content-between mt-3">
                  <button className="btn btn-primary me-2" onClick={() => handlePreviewClick(report)}>
                    Предпросмотр
                  </button>
                  <button className="btn btn-success me-2" onClick={() => openActionModal('single', report.filename)}>
                    Скачать
                  </button>
                  {!isViewOnly && (
                    <button className="btn btn-danger" onClick={() => handleDelete(report.filename)}>
                      Удалить
                    </button>
                  )}
                </div>

              </div>
            </div>
          </div>
        ))}
        {/* Sentinel */}
        <div ref={sentinelRef} style={{ height: 1, width: '100%' }} />
        {isLoading && (
          <div className="col-12 text-center mb-3 text-muted">
            Загрузка...
          </div>
        )}
        {!isLoading && !hasMore && reports.length > 0 && (
          <div className="col-12 text-center mb-3 text-muted">
            Все отчеты загружены
          </div>
        )}
      </div>
      {/* Удалена панель пагинации в пользу бесконечной прокрутки */}

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Предпросмотр отчета
            {selectedReport && (
              <button className="btn btn-success ms-3" onClick={() => openActionModal('single', selectedReport.filename)}>
                Скачать
              </button>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewLoading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Загрузка...</span>
              </div>
              <p className="mt-2 text-muted">Загрузка предпросмотра...</p>
            </div>
          )}
          {previewGenerating && (
            <div className="text-center py-5">
              <div className="spinner-border text-warning" role="status">
                <span className="visually-hidden">Генерация...</span>
              </div>
              <p className="mt-2 text-warning">Превью не найдено, создаю... Пожалуйста, подождите.</p>
            </div>
          )}
          {error && !previewLoading && !previewGenerating && (
            <div className="text-center py-5">
              <p className="text-danger">{error}</p>
            </div>
          )}
          <div
            ref={viewerRef}
            style={{
              maxHeight: '70vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: window.innerWidth <= 768 ? '8px' : '16px 0 16px 0',
              margin: '0',
              textAlign: 'left',
              width: '100%',
              display: (previewLoading || previewGenerating || error) ? 'none' : 'block'
            }}
          >
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showActionModal} onHide={() => setShowActionModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {actionModalConfig?.type === 'single' ? 'Скачать / Отправить отчёт' :
             actionModalConfig?.type === 'monthly' ? 'Скачать / Отправить за месяц' :
             actionModalConfig?.type === 'period' ? 'Скачать / Отправить за период' :
             'Скачать / Отправить выбранное'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div className="d-flex gap-3 justify-content-center">
            <button className="btn btn-success btn-lg" onClick={handleActionDownload}>
              Скачать
            </button>
            <button className="btn btn-telegram btn-lg" onClick={handleActionTelegram}>
              Отправить в Telegram
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showTelegramModal} onHide={() => setShowTelegramModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Telegram</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <p className="mb-0" style={{ fontSize: '1.1em' }}>
            Отчёты отправлены на загрузку в Telegram, может занять какое-то время, но они точно дойдут 😊
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-primary" onClick={() => setShowTelegramModal(false)}>
            Ок
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDatePicker} onHide={() => setShowDatePicker(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Выберите интервал дат</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">От:</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">До:</label>
            <input
              type="date"
              className="form-control"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowDatePicker(false)}
          >
            Отмена
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              if (dateRange.startDate && dateRange.endDate) {
                setIsDateFiltered(true);
                setShowDatePicker(false);
                fetchReports();
              }
            }}
          >
            Выгрузить отчеты
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default Reports;
